import type { SidebarMenuItem } from '../types';

function trimTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Exact match for "/", segment-boundary prefix match for everything else.
 *
 * A bare `startsWith` would light up "/templates" for "/templates-archive",
 * which is a different page.
 */
export function isPathActive(itemPath: string, pathname: string): boolean {
  const item = trimTrailingSlash(itemPath);
  const current = trimTrailingSlash(pathname);
  if (item === '/') return current === '/';
  return current === item || current.startsWith(`${item}/`);
}

/** True when the item itself, or anything beneath it, matches the route. */
export function isItemActive(item: SidebarMenuItem, pathname: string): boolean {
  if (isPathActive(item.path, pathname)) return true;
  return (item.children ?? []).some((child) => isItemActive(child, pathname));
}

/**
 * Ids of every ancestor holding the active route, so a deep link arrives with
 * its branch already open.
 */
export function collectExpandableIds(
  items: readonly SidebarMenuItem[],
  pathname: string,
): string[] {
  const ids: string[] = [];

  const walk = (nodes: readonly SidebarMenuItem[]): void => {
    nodes.forEach((node) => {
      const children = node.children ?? [];
      if (children.length === 0) return;
      if (children.some((child) => isItemActive(child, pathname))) {
        ids.push(node.id);
      }
      walk(children);
    });
  };

  walk(items);
  return ids;
}
